import React, { useEffect, useState } from 'react';
import '../styles/LocksList.css';

function LocksList({ locks, onWithdraw, onAddFunds, onEmergencyWithdraw, onApproveMultiSig, account, disabled }) {
  const [timeRemaining, setTimeRemaining] = useState({});
  const [addAmount, setAddAmount] = useState({});
  const [showNft, setShowNft] = useState({});
  const [expandedLock, setExpandedLock] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    const updateCountdown = () => {
      const newTimeRemaining = {};
      locks.forEach((lock) => {
        if (lock.isActive) {
          const unlockDate = new Date(parseInt(lock.unlockTime) * 1000);
          const now = new Date();
          const diff = unlockDate - now;

          if (diff > 0) {
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            newTimeRemaining[lock.id] = `${days}d ${hours}h ${minutes}m`;
          } else {
            newTimeRemaining[lock.id] = 'Ready to unlock';
          }
        }
      });
      setTimeRemaining(newTimeRemaining);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [locks]);

  const getTokenName = (address) => {
    const addr = address.toLowerCase();
    const names = {
      '0xc7de83ac4c2971dca2828ea0782b5a9706d30598': 'USDC',
      '0x9e00a7f73e9782e20a28898eb80400dc4ea33e2b': 'USDT',
      '0x8c00df228db94e67ef03dfdadcd68c2931c0effa': 'DAI',
      '0x8dde1de10af0d0b69e869076d8804d84d1f3f099': 'EURC',
    };
    return names[addr] || address.substring(0, 6) + '...';
  };

  const getLockStatus = (lock) => {
    if (lock.withdrawn) return 'withdrawn';
    const unlockTimePassed = Date.now() >= parseInt(lock.unlockTime) * 1000;
    if (unlockTimePassed || lock.isReady) return 'ready';
    return 'active';
  };

  // Count locks by status
  const counts = locks.reduce((acc, lock) => {
    const s = getLockStatus(lock);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  // Filter locks
  const filteredLocks = activeFilter === 'all'
    ? locks
    : locks.filter(lock => getLockStatus(lock) === activeFilter);

  if (locks.length === 0) {
    return null;
  }

  return (
    <div className="locks-list">
      <div className="locks-filter-tabs">
        {[
          { key: 'all', label: 'All', count: locks.length },
          { key: 'active', label: 'Active', count: counts.active || 0 },
          { key: 'ready', label: 'Ready', count: counts.ready || 0 },
          { key: 'withdrawn', label: 'Withdrawn', count: counts.withdrawn || 0 },
        ].map(tab => (
          <button
            key={tab.key}
            className={`filter-tab ${activeFilter === tab.key ? 'active' : ''} ${tab.key !== 'all' ? tab.key : ''}`}
            onClick={() => setActiveFilter(tab.key)}
          >
            {tab.label} <span className="tab-count">{tab.count}</span>
          </button>
        ))}
      </div>

      {filteredLocks.map((lock) => {
        const unlockTimePassed = !lock.withdrawn && Date.now() >= parseInt(lock.unlockTime) * 1000;
        const isStillLocked = !lock.withdrawn && !unlockTimePassed;
        const isReady = !lock.withdrawn && (lock.isReady || unlockTimePassed);
        const isExpanded = expandedLock === lock.id;

        return (
        <div
          key={lock.id}
          className={`lock-card ${lock.withdrawn ? 'withdrawn' : isStillLocked ? 'active' : 'unlocked'} ${isExpanded ? 'expanded' : 'compact'}`}
        >
          {/* Compact row — always visible, click to expand */}
          <div className="lock-compact-row" onClick={() => setExpandedLock(isExpanded ? null : lock.id)}>
            <div className="compact-left">
              <span className="compact-name">{lock.name || `Lock #${lock.id}`}</span>
              <span className="lock-token-badge">{getTokenName(lock.token)}</span>
              {lock.flexible ? (
                <span className="lock-type-badge flexible-badge">Flexible</span>
              ) : parseFloat(lock.savingsGoal) > 0 ? (
                <span className="lock-type-badge savings-badge">Savings</span>
              ) : (
                <span className="lock-type-badge hardlock-badge">Hard Lock</span>
              )}
            </div>
            <div className="compact-right">
              <span className="compact-amount">{lock.amount} {getTokenName(lock.token)}</span>
              {isStillLocked && timeRemaining[lock.id] && (
                <span className="compact-countdown">{timeRemaining[lock.id]}</span>
              )}
              <span className={`status ${lock.withdrawn ? 'withdrawn' : isStillLocked ? 'locked' : 'unlocked'}`}>
                {lock.withdrawn ? 'Withdrawn' : isStillLocked ? 'Locked' : 'Ready'}
              </span>
              <span className={`expand-arrow ${isExpanded ? 'open' : ''}`}>▾</span>
            </div>
          </div>

          {/* Expanded details — only visible when expanded */}
          {isExpanded && (
            <div className="lock-expanded">
              <div className="lock-header">
                <div className="lock-header-right">
                  {lock.nftImage && (
                    <button
                      className="btn-nft-toggle"
                      onClick={(e) => { e.stopPropagation(); setShowNft(prev => ({ ...prev, [lock.id]: !prev[lock.id] })); }}
                      title="View NFT"
                    >
                      NFT #{lock.id}
                    </button>
                  )}
                </div>
              </div>

              {showNft[lock.id] && lock.nftImage && (
                <div className="nft-preview">
                  <img src={lock.nftImage} alt={`NFT #${lock.id}`} className="nft-image" />
                  <a
                    href={`https://testnets.opensea.io/assets/sepolia/${process.env.REACT_APP_CONTRACT_ADDRESS}/${lock.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="nft-opensea-link"
                  >
                    View on OpenSea
                  </a>
                </div>
              )}

              <div className="lock-details">
                <div className="detail">
                  <label>Amount</label>
                  <strong>{lock.amount} {getTokenName(lock.token)}</strong>
                </div>

                {parseFloat(lock.savingsGoal) === 0 && (
                  <div className="detail">
                    <label>Unlocks</label>
                    <strong>{new Date(parseInt(lock.unlockTime) * 1000).toLocaleDateString()}</strong>
                  </div>
                )}

                {isStillLocked && parseFloat(lock.savingsGoal) === 0 && (
                  <div className="detail countdown">
                    <label>Time Left</label>
                    <strong>{timeRemaining[lock.id] || 'Calculating...'}</strong>
                  </div>
                )}

                {parseFloat(lock.savingsGoal) > 0 && (
                  <>
                    <div className="detail">
                      <label>Goal</label>
                      <strong>{lock.savingsGoal} {getTokenName(lock.token)}</strong>
                    </div>
                    <div className="detail detail-full">
                      <label>Progress</label>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${lock.savingsProgress}%` }}
                        />
                      </div>
                      <small>{lock.currentSavings} / {lock.savingsGoal} ({lock.savingsProgress}%)</small>
                    </div>
                    {parseFloat(lock.savingsRemaining) > 0 && (
                      <div className="detail">
                        <small>💰 {lock.savingsRemaining} more to reach goal</small>
                      </div>
                    )}
                  </>
                )}

                <div className="detail">
                  <label>Beneficiary</label>
                  <small>{lock.beneficiary.substring(0, 6)}...{lock.beneficiary.substring(38)}</small>
                </div>

                {lock.multiSig && (
                  <div className="multisig-info">
                    <div className="multisig-progress">
                      <strong>{lock.multiSig.current}/{lock.multiSig.required}</strong> approvals
                      {lock.multiSig.current >= lock.multiSig.required
                        ? <span className="multisig-ready"> — Approved</span>
                        : <span className="multisig-pending"> — Pending</span>}
                    </div>
                    {lock.multiSig.current < lock.multiSig.required && (
                      <button
                        onClick={() => onApproveMultiSig(lock.id)}
                        disabled={disabled}
                        className="btn-approve-multisig"
                      >
                        Approve
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="lock-actions">
                {isReady ? (
                  <button
                    onClick={() => onWithdraw(lock.id)}
                    disabled={disabled}
                    className="btn-withdraw"
                  >
                    Withdraw
                  </button>
                ) : isStillLocked ? (
                  <div className="add-funds-section">
                    <button disabled className="btn-disabled">
                      {parseFloat(lock.savingsGoal) > 0 && parseFloat(lock.savingsRemaining) > 0
                        ? `${lock.savingsRemaining} more to goal`
                        : 'Locked'}
                    </button>
                    {!lock.withdrawn && (
                      <div className="add-funds-form">
                        <input
                          type="number"
                          placeholder="Amount to add"
                          value={addAmount[lock.id] || ''}
                          onChange={(e) => setAddAmount({ ...addAmount, [lock.id]: e.target.value })}
                          step="0.01"
                          min="0"
                          disabled={disabled}
                          className="add-funds-input"
                        />
                        <button
                          onClick={() => {
                            onAddFunds(lock.id, addAmount[lock.id], lock.token);
                            setAddAmount({ ...addAmount, [lock.id]: '' });
                          }}
                          disabled={disabled || !addAmount[lock.id] || parseFloat(addAmount[lock.id]) <= 0}
                          className="btn-add-funds"
                        >
                          Add
                        </button>
                      </div>
                    )}
                    {lock.flexible && (
                      <button
                        onClick={() => {
                          if (window.confirm('Emergency withdraw will deduct a 10% penalty. Are you sure?')) {
                            onEmergencyWithdraw(lock.id);
                          }
                        }}
                        disabled={disabled}
                        className="btn-emergency"
                      >
                        Emergency Withdraw (10% penalty)
                      </button>
                    )}
                  </div>
                ) : (
                  <button disabled className="btn-disabled">
                    Withdrawn
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      );
      })}
    </div>
  );
}

export default LocksList;
